// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import {
    AddRaterRequest,
    AddRaterResult,
    AssessmentItem,
    DeleteRaterRequest,
    EmailRequest,
    Individual,
    LanguageList,
    LanguageRequest,
    RaterDetails,
    RaterSwitchRequest,
    RaterType,
    SearchRaters,
} from "./types/assessmentAPITypes";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

// Define a service using a base URL and expected endpoints
export const cclTokenedEnterpriseRaterApi = createApi({
    reducerPath: "cclTokenedEnterpriseRaterApi",
    tagTypes: ["SessionRaters", "ParticipantRaters"],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            //we should probably change this.
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        getRaterDetailsByParticipantID: builder.mutation<RaterDetails[], number>({
            query: (esikey) => ({
                url: `ccl-access2/RaterManagement/Raters/${esikey}`,
                providesTags: (_: any, __: any, esikey: number) => [
                    { type: "ParticipantRaters", id: esikey },
                ],
                method: "POST",
                body: esikey,
            }),
        }),

        getRaterTypesByInstrumentKey: builder.query<RaterType[], number>({
            query: (id) => `ccl-access2/RaterManagement/RaterTypes/${id}`,
        }),

        updateRaterSwitch: builder.mutation<boolean, RaterSwitchRequest>({
            query: (request) => ({
                url: `ccl-access2/RaterManagement/UpdateRaterSwitch/${request.rmkey}/${request.raterTypeKey}/${request.formActualID}`,
                method: "POST",
                body: request,
            }),
            invalidatesTags: (_, __, request: RaterSwitchRequest) => [
                { type: "ParticipantRaters", id: request.esiKey },
                { type: "SessionRaters", id: request.esKey },
            ],
        }),

        DeleteRaterById: builder.mutation<number, DeleteRaterRequest>({
            query: (request) => ({
                url: `ccl-access2/RaterManagement/DeleteRaterById/${request.rmkey}`,
                method: "DELETE",
                body: request,
            }),
            invalidatesTags: (_, __, request: DeleteRaterRequest) => [
                { type: "ParticipantRaters", id: request.esiKey },
                { type: "SessionRaters", id: request.esKey },
            ],
        }),

        ReSendRaterReminders: builder.mutation<string, EmailRequest[]>({
            query: (request) => ({
                url: "ccl-access2/RaterManagement/ReSendRaterReminders",
                method: "POST",
                body: request,
            }),
        }),

        AddRaters: builder.mutation<AddRaterResult, AddRaterRequest[]>({
            query: (request) => ({
                url: "ccl-access2/RaterManagement/Raters",
                method: "POST",
                body: request,
            }),
            invalidatesTags: (_, __, request: AddRaterRequest[]) => [
                ...request.map(({ participantId }) => ({
                    type: "ParticipantRaters" as const,
                    id: participantId,
                })),
            ],
        }),

        GetLanguagesByInstrumentId: builder.query<LanguageList[], LanguageRequest>({
            query: (request) =>
                `ccl-access2/RaterManagement/RaterLanguages/${request.Instrument}/${request.PreferredLanguageId}`,
        }),

        SearchRaters: builder.mutation<Individual, SearchRaters>({
            query: (request) => ({
                url: `ccl-access2/RaterManagement/Raters/SearchByEmail`,
                method: "POST",
                body: request,
            }),
        }),

        ReSendRaterInvitation: builder.mutation<string, EmailRequest[]>({
            query: (request) => ({
                url: "ccl-access2/RaterManagement/ReSendRaterInvitation",
                method: "POST",
                body: request,
            }),
        }),

        GetAssessmentByParticipant: builder.query<AssessmentItem[], number>({
            query: (esiKey) =>
                `ccl-access2/RaterManagement/Raters/GetAssessmentByParticipantId/${esiKey}`,
        }),

        getSessionRatersByEsKey: builder.mutation<RaterDetails[], number>({
            query: (eskey) => ({
                url: `ccl-access2/RaterManagement/ParticipantRaters/${eskey}`,
                providesTags: (_: any, __: any, eskey: number) => [
                    { type: "SessionRaters", id: eskey },
                ],
                method: "POST",
                body: eskey,
            }),
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetRaterDetailsByParticipantIDMutation,
    useGetRaterTypesByInstrumentKeyQuery,
    useLazyGetRaterTypesByInstrumentKeyQuery,
    useUpdateRaterSwitchMutation,
    useDeleteRaterByIdMutation,
    useReSendRaterRemindersMutation,
    useAddRatersMutation,
    useLazyGetLanguagesByInstrumentIdQuery,
    useSearchRatersMutation,
    useReSendRaterInvitationMutation,
    useLazyGetAssessmentByParticipantQuery,
    useGetSessionRatersByEsKeyMutation,
} = cclTokenedEnterpriseRaterApi;
